import { useState, useEffect } from 'react';

import type { Node } from 'reactflow';
import { v4 as uuidv4 } from 'uuid';
import { Box, Tooltip } from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ListIcon from '@mui/icons-material/List';
import { Drawer, Table } from 'components';
import {
  IResultTables,
  IResultTable,
  ITableHandlers,
  IResultsData,
  ExtendedDataTitle,
} from 'types';

import * as S from '../Drawer.styled';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  content: Node;
}

interface IExtendedContent {
  data: IResultsData[];
  title: ExtendedDataTitle;
}

export const ResultDrawer = ({ isOpen, onClose, content }: IProps) => {
  const [extendedContent, setExtendedContent] = useState<IExtendedContent | null>(null);

  const handleResults = () => {
    setExtendedContent((prev) =>
      prev?.title === ExtendedDataTitle.RESULTS
        ? null
        : { data: content.data.resultsData, title: ExtendedDataTitle.RESULTS }
    );
  };

  const handleDefinition = () => {
    setExtendedContent((prev) =>
      prev?.title === ExtendedDataTitle.DEFINITIONS
        ? null
        : { data: content.data.definitionsData, title: ExtendedDataTitle.DEFINITIONS }
    );
  };

  const tableHandlers: Array<ITableHandlers<IResultTable>> = [
    {
      handler: handleResults,
      icon: (
        <Tooltip title="Results" arrow>
          <ListIcon />
        </Tooltip>
      ),
    },
    {
      handler: handleDefinition,
      icon: (
        <Tooltip title="Definitions" arrow>
          <AutoStoriesIcon />
        </Tooltip>
      ),
    },
  ];

  useEffect(
    () => () => {
      setExtendedContent(null);
    },
    [onClose]
  );

  return (
    <Drawer
      width={435}
      isOpen={isOpen}
      isExtended={Boolean(extendedContent)}
      onClose={() => {
        onClose();
        setExtendedContent(null);
      }}
    >
      {extendedContent && (
        <S.Content>
          <S.Title variant="h5">{extendedContent.title}</S.Title>
          {extendedContent.data.map(({ image }) => (
            <Box key={`result-details-${uuidv4()}`}>
              <S.ArticleImage src={image} />
              <S.Link>show source</S.Link>
            </Box>
          ))}
        </S.Content>
      )}
      <S.Content>
        <S.Title variant="h5">{content.data.label} details</S.Title>
        {content.data.description && <S.Description>{content.data.description}</S.Description>}
        {content.data.tables.map(({ tableTitle, tableData }: IResultTables) => (
          <S.TableWrapper key={`table-${uuidv4()}`}>
            {tableTitle && <S.TableTitle>{tableTitle}:</S.TableTitle>}
            <Table<IResultTable> data={tableData} tableHandlers={tableHandlers} />
          </S.TableWrapper>
        ))}
      </S.Content>
    </Drawer>
  );
};
